import { lazy, Suspense, useEffect, useState } from 'react';

import { Button, Input, Select, Spinner, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';

import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_LONG, DURATION_TOAST, SMALL_SCREEN_WIDTH_LIMIT } from '@/common/constants';
import MonthSelector from '@/components/Dashboard/MonthSelector';
import { getCurrentMonth } from '@/utils/date';
import { getInspectionsStatusTable, InspectionStatusData } from '@/services/dashboardService';
import useSmallScreen from '@/utils/UseDeviceSize';

import '../styles/ListPage.css'
import { getDataRef } from '@/services/refDataService';
import { Option } from "@/utils/select";

const TableInspectionStatus = lazy(() => import('@/components/Tables/TableInspectionStatus'));

const InspectionStatusList = () => {
    const toast = useToast();
    const {year: currentYear, month: currentMonth} = getCurrentMonth();
    const isSmallScreen = useSmallScreen();
    const defaultPageSize = (window.innerWidth <= SMALL_SCREEN_WIDTH_LIMIT) ? DEFAULT_PAGE_SIZE : DEFAULT_PAGE_SIZE_LONG;

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(defaultPageSize);
    const [dataList, setDataList] = useState<InspectionStatusData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);
    const [search, setSearch] = useState<string>('');
    const [listMarket, setListMarket] = useState<Option[]>([]);
    const [market, setMarket] = useState('')

    const showToast = (error: any) => {
        toast({
            title: error?.status || 'Error',
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const getData = async () => {
        setIsLoading(true);
        try {
            const response = await getInspectionsStatusTable(month, year, search, market, currentPageNumber, currentPageSize);

            if (response.status === HttpStatusCode.Ok) {
                const data = response.data as InspectionStatusData[];
                setDataList(data);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
            } else {
                showToast(response);
            }
        } catch (error) {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
        } finally {
            setIsLoading(false);
        }
    };

    const getListRef = async (type: string) => {
        const response = await getDataRef(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.value,
                label: item.name,
            }));

            if (type === 'market') {
                setListMarket(formattedData);
            }
        } else {
            toast({
                title: 'Failed to get data ref.',
                description: "We unabled to get this data.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setListMarket([]);
        }
    }

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await Promise.all([
                    getListRef('market'),
                ])

            } catch (_error) {
                console.log(_error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        getData();
    }, [currentPageNumber, currentPageSize]);

    return (
        <>
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className='table-filter-input filter-input-width-5'>
                            <Input
                                placeholder="Search"
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="table-filter-input filter-input-width-3">
                            <Select
                                placeholder="All Market"
                                onChange={(e) => setMarket(e.target.value)}

                            >
                                {listMarket.map((market) => (
                                    <option key={market.value} value={market.value}>
                                        {market.label}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <MonthSelector month={month} year={year} onChange={(newMonth, newYear) => { setMonth(newMonth); setYear(newYear) }} isStacked={isSmallScreen} />
                        <div>
                            <Button
                                className='table-search-button'
                                bgColor={COLORS.BUTTON.BACKGROUND}
                                color={COLORS.BUTTON.FOREGROUND}
                                borderRadius={10}
                                onClick={() => getData()}
                            >
                                Search
                            </Button>
                        </div>
                    </div>
                </div>
                <Suspense fallback={<Spinner size="xl" />}>
                    <TableInspectionStatus
                        dataList={dataList}
                        isLoading={isLoading}
                        currentPageSize={currentPageSize}
                        handleChangePageSize={setCurrentPageSize}
                        handleChangePageNumber={setCurrentPageNumber}
                        currentPageNumber={currentPageNumber}
                        totalPageNumber={totalPageNumber}
                        hasNextPage={hasNextPage}
                    />
                </Suspense>
            </div>
        </>
    );
};

export default InspectionStatusList;
